import { useState, useMemo } from 'react'
import { components } from 'react-select'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import moment from 'moment'

import { stockAccolades } from '../../accolades/accolades'
import Select from '../common/form/Select'
import Input from '../common/form/Input'

import classes from './AccoladeSelect.module.scss'

const AccoladeSelect = ({ allAccolades, currentAccolades, onAddAccolade, onDeleteAccolade }) => {
  const { t } = useTranslation()
  const [changeDate, setChangeDate] = useState(moment())

  const availableOptions = useMemo(() => {
    const currentAccoladeIds = currentAccolades.map(acc => acc.accolade?.id || acc.accolade_id)
    return allAccolades.filter(acc => !currentAccoladeIds.includes(acc.id))
  }, [allAccolades, currentAccolades])

  const handleSelectAccolade = (option) => {
    if (option) {
      onAddAccolade({
        accolade_id: option.id,
        issued_date: changeDate.format('YYYY-MM-DD'),
      })
    }
  }

  /* eslint-disable react/prop-types */
  const Option = ({ data, ...props }) => {
    const accoladeBadge = stockAccolades[data.name]?.badge

    return (
      <components.Option {...props}>
        <div className={classes.optionContainer}>
          <div className={classes.accoladeInfo}>
            {accoladeBadge && (
              <div
                className={classes.accoladeBadge}
                style={{ backgroundImage: `url(${accoladeBadge})` }}
              />
            )}
            <span className={classes.accoladeName}>{data.name}</span>
          </div>
        </div>
      </components.Option>
    )
  }
  /* eslint-enable react/prop-types */

  return (
    <div>
      {currentAccolades.length > 0 && (
        <>
          <p className='font-weight-bold'>{t('coaches.accoladeSelect.currentAccolades')}</p>
          <div className='d-flex flex-wrap mb-4'>
            {currentAccolades.map((acc) => {
              const accoladeId = acc.accolade?.id || acc.accolade_id
              const accoladeName = acc.accolade?.name || allAccolades.find(a => a.id === accoladeId)?.name
              const accoladeBadge = accoladeName && stockAccolades[accoladeName]?.badge

              return (
                <div key={acc.id} className={classes.accoladeCard}>
                  <div className={classes.accoladeContent}>
                    {accoladeBadge && (
                      <div className={classes.accoladeBadge} style={{ backgroundImage: `url(${accoladeBadge})` }} />
                    )}
                    {acc.issued_date && (
                      <p className={classes.issuedDate}>
                        {moment(acc.issued_date).format('MM/DD/YYYY')}
                      </p>
                    )}
                  </div>
                  <button className={classes.deleteButton} onClick={() => onDeleteAccolade(acc)}>
                    <i className='fa fa-times' />
                  </button>
                </div>
              )
            })}
          </div>
        </>
      )}

      <div className='mb-3'>
        <label htmlFor='accolade-selector'>{t('coaches.accoladeSelect.selectAccolade')}</label>
        <Select
          name='accolade-selector'
          noOptionsMessage={() => t('common.noOptions')}
          value={null}
          options={availableOptions}
          components={{ Option }}
          onChange={handleSelectAccolade}
          placeholder={t('coaches.accoladeSelect.selectAccolade')}
          isSearchable={false}
          menuPlacement='auto'
          minMenuHeight={300}
          menuPosition='fixed'
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
        />
      </div>

      <div>
        <label htmlFor='issued_date'>{t('coaches.accoladeSelect.changeDate')}</label>
        <Input
          type='date'
          name='issued_date'
          selected={changeDate}
          onChange={setChangeDate}
          maxDate={moment()}
          dateFormat='MM/dd/yyyy'
          placeholderText='MM/DD/YYYY'
        />
      </div>
    </div>
  )
}

AccoladeSelect.propTypes = {
  allAccolades: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  currentAccolades: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    accolade_id: PropTypes.number,
    issued_date: PropTypes.string,
  })),
  onAddAccolade: PropTypes.func.isRequired,
  onDeleteAccolade: PropTypes.func.isRequired,
}

export default AccoladeSelect
